<script setup lang="ts">
/**
 * The visual body of an announcement: cover, title, body, step dots, actions.
 *
 * PURE PRESENTATION. This component must never import a store, `usePage`,
 * `route()`, or axios — it is rendered in two places, and only one of them has
 * any of those:
 *   - AnnouncementModal.vue wraps it in a Dialog and feeds it server state
 *   - the admin builder's preview pane feeds it UNSAVED draft state
 *
 * The moment it reaches for anything ambient, the preview stops being an
 * honest preview. `tests/js` mounts it with plain props and no app context to
 * keep that true.
 */
import { computed, nextTick, ref, watch } from 'vue'
import Button from '@/shadcn/ui/button/Button.vue'
import type { AnnouncementStep } from '@/types/announcement'

const props = defineProps<{
    steps: AnnouncementStep[]
    stepIndex: number
    /** Preview mode: no focusable controls, since the frame itself is inert. */
    static?: boolean
    /**
     * Author mode. The copy becomes editable in place, so what you type is
     * literally what people read — same file, same type, same spacing. Still
     * no store, no route(), no fetch: it emits and the page persists.
     */
    editable?: boolean
}>()

const emit = defineEmits<{
    (e: 'next'): void
    (e: 'back'): void
    (e: 'dismiss'): void
    (e: 'cta', step: AnnouncementStep): void
    (e: 'update:title', value: string): void
    (e: 'update:body', value: string): void
    (e: 'update:ctaLabel', value: string): void
    (e: 'pick-image'): void
}>()

const step = computed<AnnouncementStep | null>(() => props.steps[props.stepIndex] ?? null)
const total = computed(() => props.steps.length)
const isFirst = computed(() => props.stepIndex === 0)
const isLast = computed(() => props.stepIndex >= total.value - 1)

// "Got it" on the last step unless the author wrote their own label — the
// default has to read like an ending, not like there's more coming.
const ctaLabel = computed(() => {
    const authored = step.value?.cta_label?.trim()
    if (authored) return authored
    return isLast.value ? 'Got it' : 'Continue'
})

// The builder blocks over-length copy, but a modal is the wrong place to
// discover that something slipped through.
const TITLE_LIMIT = 60
const BODY_LIMIT = 200

function clamp(value: string | null | undefined, limit: number): string {
    const text = (value ?? '').trim()
    return text.length > limit ? `${text.slice(0, limit - 1)}…` : text
}

const title = computed(() => clamp(step.value?.title, TITLE_LIMIT))
const body = computed(() => clamp(step.value?.body, BODY_LIMIT))

const titleField = ref<HTMLTextAreaElement | null>(null)
const bodyField = ref<HTMLTextAreaElement | null>(null)

/** Grow a textarea to its content so the card never scrolls internally. */
function autosize(el: HTMLTextAreaElement | null): void {
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
}

// Sized after the step changes, since the same fields are reused across steps.
watch(
    () => [props.stepIndex, step.value?.title, step.value?.body],
    () => {
        if (!props.editable) return
        void nextTick(() => {
            autosize(titleField.value)
            autosize(bodyField.value)
        })
    },
    { immediate: true },
)

function onInput(event: Event, field: 'title' | 'body' | 'ctaLabel'): void {
    const el = event.target as HTMLTextAreaElement
    autosize(el)
    if (field === 'title') emit('update:title', el.value)
    else if (field === 'body') emit('update:body', el.value)
    else emit('update:ctaLabel', el.value)
}

/**
 * Only the last step can send you somewhere. Earlier steps advance, because a
 * link partway through would strand every step after it: following it leaves,
 * and the only alternative would be a second button that does what the close
 * control already does.
 */
const hasDestination = computed(() => isLast.value && Boolean(step.value?.cta_href))

function onPrimary(): void {
    if (!isLast.value) {
        emit('next')
        return
    }
    if (hasDestination.value && step.value) {
        emit('cta', step.value)
        return
    }
    emit('dismiss')
}
</script>

<template>
    <div v-if="step" class="c-announcement">
        <component
            :is="editable ? 'button' : 'div'"
            v-if="step.image_url || editable"
            :type="editable ? 'button' : undefined"
            class="c-announcement__cover"
            :class="editable ? 'c-announcement__cover--editable' : ''"
            @click="editable && emit('pick-image')"
        >
            <img
                v-if="step.image_url"
                :src="step.image_url"
                :alt="step.image_alt ?? ''"
                class="h-full w-full object-cover"
            />
            <span v-else class="c-announcement__cover-empty">Add a cover image</span>
            <span v-if="editable && step.image_url" class="c-announcement__cover-hint">Replace</span>
        </component>

        <div class="c-announcement__body">
            <textarea
                v-if="editable"
                ref="titleField"
                :value="step.title"
                rows="1"
                :maxlength="TITLE_LIMIT"
                placeholder="A line worth reading"
                aria-label="Title"
                class="c-announcement__field c-announcement__field--title"
                @input="onInput($event, 'title')"
            ></textarea>
            <p v-else class="c-announcement__title">{{ title }}</p>

            <textarea
                v-if="editable"
                ref="bodyField"
                :value="step.body ?? ''"
                rows="2"
                :maxlength="BODY_LIMIT"
                placeholder="What it does for them, in a sentence or two."
                aria-label="Body"
                class="c-announcement__field c-announcement__field--body"
                @input="onInput($event, 'body')"
            ></textarea>
            <p v-else-if="body" class="c-announcement__text">{{ body }}</p>

            <div v-if="total > 1" class="c-announcement__dots" aria-hidden="true">
                <span
                    v-for="(_, i) in steps"
                    :key="i"
                    class="c-announcement__dot"
                    :class="i === stepIndex ? 'c-announcement__dot--active' : ''"
                ></span>
            </div>

            <div class="c-announcement__actions">
                <Button
                    v-if="!editable"
                    size="lg"
                    class="w-full"
                    :tabindex="props.static ? -1 : undefined"
                    @click="onPrimary"
                >
                    {{ ctaLabel }}
                </Button>
                <div v-else class="c-announcement__cta-edit">
                    <input
                        :value="step.cta_label ?? ''"
                        :placeholder="isLast ? 'Got it' : 'Continue'"
                        maxlength="80"
                        aria-label="Button label"
                        class="c-announcement__field c-announcement__field--cta"
                        @input="onInput($event, 'ctaLabel')"
                    />
                </div>
                <Button
                    v-if="!isFirst"
                    variant="ghost"
                    size="sm"
                    class="w-full"
                    :tabindex="props.static ? -1 : undefined"
                    @click="emit('back')"
                >
                    Back
                </Button>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
/**
 * The card clips its own corners rather than the dialog doing it, so the
 * dialog is free to let the close control sit outside its edge.
 */
.c-announcement {
    overflow: hidden;
    border-radius: inherit;
    /**
     * The runtime renders in a portal at body level; the author view renders
     * inside a Bootstrap-styled admin page. Pinning the base here means the
     * two cannot differ by whatever the surrounding page happens to set.
     */
    font-size: 14px;
    line-height: 1.5;
    text-align: left;
}

/**
 * All spacing lives here rather than in Tailwind utilities, deliberately.
 *
 * Two global layers fight utilities inside a `[data-shadcn]` container:
 * `shadcn-tailwind.scss` re-declares every padding/margin utility with
 * `!important`, and Bootstrap sets `p { margin-bottom: 1rem }`. Between them,
 * `p-8` never landed and the flex gap stacked with a paragraph margin. Naming
 * the elements and setting the values here takes both out of the argument.
 */
.c-announcement__body {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 32px;
}

.c-announcement__body p {
    margin: 0;
}

.c-announcement__dots {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 12px;
}

.c-announcement__actions {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 24px;
}

.c-announcement__cover {
    display: block;
    width: 100%;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    background: hsl(var(--tw-muted));
    position: relative;
}

.c-announcement__cover--editable {
    cursor: pointer;
}

.c-announcement__cover-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    font-size: 13px;
    color: hsl(var(--tw-muted-foreground));
}

.c-announcement__cover-hint {
    position: absolute;
    right: 8px;
    bottom: 8px;
    padding: 3px 8px;
    border-radius: 6px;
    background: rgba(0, 0, 0, 0.55);
    color: #fff;
    font-size: 11px;
    opacity: 0;
    transition: opacity 150ms ease;
}

.c-announcement__cover--editable:hover .c-announcement__cover-hint {
    opacity: 1;
}

/**
 * Editable fields borrow the exact type of the text they replace, so the
 * author is looking at the finished thing rather than a form that resembles
 * it. Chrome appears on focus only.
 */
.c-announcement__field {
    width: 100%;
    border: 0;
    outline: none;
    resize: none;
    background: transparent;
    padding: 2px 4px;
    margin: -2px -4px;
    border-radius: 4px;
    font: inherit;
    overflow: hidden;
}

.c-announcement__field:hover {
    background: hsl(var(--tw-muted));
}

.c-announcement__field:focus {
    background: hsl(var(--tw-muted));
    box-shadow: 0 0 0 2px hsl(var(--tw-ring) / 0.4);
}

/**
 * One set of type declarations, worn by both the read-only text and the field
 * that replaces it in author mode. Styling the two through different systems
 * (Tailwind classes here, explicit CSS there) is how a preview drifts from the
 * thing it claims to preview.
 */
.c-announcement__title,
.c-announcement__field--title {
    font-size: 18px;
    font-weight: 600;
    line-height: 1.375;
    letter-spacing: -0.01em;
    color: hsl(var(--tw-foreground));
}

.c-announcement__text,
.c-announcement__field--body {
    font-size: 14px;
    font-weight: 400;
    line-height: 1.625;
    color: hsl(var(--tw-muted-foreground));
}

.c-announcement__cta-edit {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 44px;
    border-radius: 6px;
    background: theme('colors.moss.DEFAULT');
    padding: 0 12px;
}

.c-announcement__field--cta {
    text-align: center;
    font-size: 14px;
    font-weight: 500;
    color: theme('colors.moss.foreground');
}

.c-announcement__field--cta::placeholder {
    color: rgba(245, 243, 239, 0.7);
}

.c-announcement__field--cta:hover,
.c-announcement__field--cta:focus {
    background: rgba(255, 255, 255, 0.14);
    box-shadow: none;
}

@media (prefers-reduced-motion: reduce) {
    .c-announcement__cover-hint,
    .c-announcement__field {
        transition: none;
    }
}

.c-announcement__dot {
    width: 24px;
    height: 4px;
    border-radius: 2px;
    background: hsl(var(--tw-border));
    transition: background-color 150ms ease;
}

/**
 * Moss, matching the primary button's `bg-moss` variant, so the dots and the
 * CTA read as one control rather than two. `--tw-primary` is black in this
 * theme and would not track the brand button.
 */
.c-announcement__dot--active {
    background: theme('colors.moss.DEFAULT');
}

/**
 * On a phone the card is very nearly the whole screen, so it gives back some
 * of its inset and caps the cover. A full 16/9 image plus the copy plus the
 * button does not fit a short viewport, and the button is the part that has to
 * stay reachable without scrolling.
 */
@media (max-width: 520px) {
    .c-announcement__body {
        padding: 20px;
    }

    .c-announcement__actions {
        margin-top: 20px;
    }

    .c-announcement__cover {
        max-height: 34vh;
    }
}

/* A landscape phone is short before it is narrow, so this is its own rule. */
@media (max-height: 620px) {
    .c-announcement__cover {
        max-height: 30vh;
    }
}

@media (prefers-reduced-motion: reduce) {
    .c-announcement__dot {
        transition: none;
    }
}
</style>
